import { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Container, Row, Col, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import MainNav from '@/components/layout/AppNavbar';
import WorldCard from '@/components/ui/WorldCard';
import Pagination from '@/components/ui/Pagination';
import Axios from '@/shared/api/axios';
import StatusModal from '@/components/ui/StatusModal';

/**
 * 我的世界列表頁
 * 使用 /world/get-my-list 取得資料
 * 缺少封面與內容時，先用假資料補欄位以供 WorldCard 顯示
 */
const MyWorldsPage = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const idolId = useSelector((state) => state.user.idolId);

  const [worlds, setWorlds] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  // 從 URL 讀取分頁參數
  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  // 每頁顯示 12 筆，與世界列表頁保持一致
  const itemsPerPage = 12;

  // API 非成功狀態碼統一由 StatusModal 顯示（含攔截器附加的 HTTP_xxx）
  const statusModalRef = useRef(null);
  const [currentStatusCode, setCurrentStatusCode] = useState('');

  const fetchMyWorlds = async (page = 1) => {
    if (!idolId) return;
    setIsLoading(true);

    const params = {
      page,
      limit: itemsPerPage,
      idol_id: idolId,
    };

    try {
      const res = await Axios.post('/world/get-my-list', params);
      switch (res.data.status_code) {
        case 'SYSTEM_1000': {
          const rawData = Array.isArray(res.data.data) ? res.data.data : [];

          // 取 world_media[0].url 當封面，沒有就交給 WorldCard 自己用 placeholder
          const mapped = rawData.map((item) => {
            const firstMedia =
              Array.isArray(item.world_media) && item.world_media.length > 0
                ? item.world_media[0]
                : null;

            return {
              ...item,
              world_cover_url: firstMedia?.url || item.world_cover_url || undefined,
              // world_content 直接使用後端回傳；若未來為空再考慮額外處理
            };
          });

          setWorlds(mapped);
          setTotal(typeof res.data.total === 'number' ? res.data.total : mapped.length);
          break;
        }
        default:
          setCurrentStatusCode(res.data.status_code);
          statusModalRef.current?.show();
          break;
      }
    } catch (error) {
      // NETWORK_ERROR 由全域 ServerErrorPage 處理，不另外顯示 modal
      if (error.httpErrorCode && error.httpErrorCode !== 'NETWORK_ERROR') {
        setCurrentStatusCode(error.httpErrorCode);
        statusModalRef.current?.show();
      } else if (!error.httpErrorCode) {
        console.error('Error fetching my worlds list: ', error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // 初次載入與分頁變更時，根據 URL page 參數重新抓資料
  useEffect(() => {
    fetchMyWorlds(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, idolId]);

  const handlePageChange = (page) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('page', page);
    // 換頁捲動交給 Pagination 的 scrollMode="window" 處理，避免跟 ScrollRestoration 的瞬間置頂互搶
    setSearchParams(newParams, { preventScrollReset: true });
  };

  return (
    <>
      <MainNav />
      <Container fluid="lg" className="nav-padding">
        <Row className="pt-4">
          <Col xs={12}>
            <h3 className="mb-4">{t('my_world')}</h3>
          </Col>
        </Row>

        {/* 第二列：右上角創建世界按鈕（位置對齊 WorldsPage 的搜尋框） */}
        <Row className="mb-4">
          <Col xs={12}>
            <div className="d-flex justify-content-end">
              <Button
                variant="primaryF"
                className="rounded-pill"
                href="/world-create"
              >
                <FontAwesomeIcon icon={["fas", "plus"]} className="me-2" />
                {t('create_world')}
              </Button>
            </div>
          </Col>
        </Row>

        <Row>
          <Col xs={12}>
            {/* WorldCard Grid */}
            {worlds.length > 0 ? (
              <div className="cards-grid mb-4">
                {worlds.map((world) => (
                  <WorldCard
                    key={world.world_id}
                    world={world}
                  />
                ))}
              </div>
            ) : (
              !isLoading && (
                <div className="text-center py-5 text-muted">
                  {t('no_data')}
                </div>
              )
            )}

            {/* 分頁 */}
            {total > itemsPerPage && (
              <div className="d-flex justify-content-center">
                <Pagination
                  total={total}
                  limit={itemsPerPage}
                  currentPage={currentPage}
                  onPageChange={handlePageChange}
                  scrollMode="window"
                />
              </div>
            )}
          </Col>
        </Row>
      </Container>
      <StatusModal ref={statusModalRef} statusCode={currentStatusCode} />
    </>
  );
};

export default MyWorldsPage;

